import { createWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

import {
  isPublicGameWithTeams,
  type PublicGame,
  type PublicGameWithTeams,
  type TeamState,
} from '~/domains/contest/domains/services/interfaces/propsService/api/game';
import type { PublicTeam } from '~/domains/contest/domains/services/interfaces/propsService/api/team';
import type { PublicEntity } from '~/domains/contest/domains/services/interfaces/propsService/api/entity';

import type { Team } from '../components/MarketCard/types';
import type { PublicIncludedSubCategory } from '../services/interfaces/market.types';

export type NormalizedTeam = Team & {
  eventId: string;
  rawTeam?: PublicTeam;
};

export type GameEventUpdate = {
  status?: PublicGame['status'];
  home?: Partial<TeamState>;
  away?: Partial<TeamState>;
  state?: Partial<PublicGame['state']>;
};

type EntitiesState = {
  eventsById: Map<string, PublicGame>;
  teamsById: Map<string, PublicTeam>;
  entitiesById: Map<string, PublicEntity>;
  subCategoryIconUrlsById: Map<string, string>;

  normalizedTeamsByEventAndType: Map<string, NormalizedTeam>;

  mergeEntities: (
    events: PublicGame[],
    teams: PublicTeam[],
    entities: PublicEntity[],
    subCategories?: PublicIncludedSubCategory[]
  ) => void;
  updateEvent: (eventId: string, eventUpdate: GameEventUpdate) => void;
  updateEventsBatch: (updates: Array<{ eventId: string; update: GameEventUpdate }>) => void;
  reset: () => void;
};

type EntitiesStateData = Omit<
  EntitiesState,
  'mergeEntities' | 'updateEvent' | 'updateEventsBatch' | 'reset'
>;

const initialState: EntitiesStateData = {
  eventsById: new Map(),
  teamsById: new Map(),
  entitiesById: new Map(),
  subCategoryIconUrlsById: new Map(),
  normalizedTeamsByEventAndType: new Map(),
};

function normalizeTeamData(
  teamData: PublicTeam | undefined,
  teamState: TeamState,
  event: PublicGameWithTeams
): NormalizedTeam {
  const rawScore = teamState.points ?? teamState.runs ?? 0;
  const teamName = teamData?.name ?? teamState.alias ?? 'Unknown';
  const teamAlias = teamData?.alias ?? teamState.alias ?? '';
  const teamMarket = teamData?.market ?? teamState.alias ?? '';

  return {
    id: teamState.id,
    eventId: event.id,
    abbreviation: teamAlias,
    name: teamName,
    market: teamMarket,
    iconUrl: teamData?.icon_url,
    primaryColor: teamData?.primary_color,
    score: event.status === 'SCHEDULED' ? undefined : rawScore,
    record: teamState.record,
    rawTeam: teamData,
  };
}

function computeNormalizedTeamsForEvent(
  event: PublicGameWithTeams,
  teamsById: Map<string, PublicTeam>
): { homeTeam: NormalizedTeam; awayTeam: NormalizedTeam } {
  const homeTeamData = teamsById.get(event.home.id);
  const awayTeamData = teamsById.get(event.away.id);

  const homeTeam = normalizeTeamData(homeTeamData, event.home, event);
  const awayTeam = normalizeTeamData(awayTeamData, event.away, event);

  return { homeTeam, awayTeam };
}

function computeNormalizedTeamsIncremental(
  eventsById: Map<string, PublicGame>,
  teamsById: Map<string, PublicTeam>,
  existingNormalized: Map<string, NormalizedTeam>,
  changedEventIds: Set<string>
): Map<string, NormalizedTeam> {
  if (changedEventIds.size === 0) {
    return existingNormalized;
  }

  const normalizedTeams = new Map(existingNormalized);

  for (const eventId of changedEventIds) {
    const event = eventsById.get(eventId);
    if (event && isPublicGameWithTeams(event)) {
      const { homeTeam, awayTeam } = computeNormalizedTeamsForEvent(event, teamsById);

      normalizedTeams.set(`${eventId}:home`, homeTeam);
      normalizedTeams.set(`${eventId}:away`, awayTeam);
    }
  }

  return normalizedTeams;
}

const hasRecordChanged = (prev: TeamState['record'], next: TeamState['record']): boolean => {
  if (prev === next) return false;
  if (!prev || !next) return true;

  return prev.wins !== next.wins || prev.losses !== next.losses || prev.ties !== next.ties;
};

const hasTeamStateChanged = (prev: TeamState, next: TeamState): boolean => {
  if (prev === next) return false;

  return (
    prev.id !== next.id ||
    prev.alias !== next.alias ||
    prev.points !== next.points ||
    prev.runs !== next.runs ||
    prev.primary_color !== next.primary_color ||
    prev.team_color !== next.team_color ||
    hasRecordChanged(prev.record, next.record)
  );
};

const hasEventChanged = (prev: PublicGame | undefined, next: PublicGame): boolean => {
  if (!prev) return true;
  if (prev === next) return false;
  if (prev.status !== next.status) return true;

  const prevHasTeams = isPublicGameWithTeams(prev);
  const nextHasTeams = isPublicGameWithTeams(next);
  if (!prevHasTeams || !nextHasTeams) return prevHasTeams !== nextHasTeams;

  return hasTeamStateChanged(prev.home, next.home) || hasTeamStateChanged(prev.away, next.away);
};

const hasTeamChanged = (prev: PublicTeam | undefined, next: PublicTeam): boolean => {
  if (!prev) return true;
  if (prev === next) return false;

  return (
    prev.id !== next.id ||
    prev.name !== next.name ||
    prev.league !== next.league ||
    prev.alias !== next.alias ||
    prev.market !== next.market ||
    prev.primary_color !== next.primary_color ||
    prev.icon_url !== next.icon_url
  );
};

function mergeSubCategoryIconUrls(
  existing: Map<string, string>,
  subCategories: PublicIncludedSubCategory[] | undefined
): Map<string, string> {
  const additions = (subCategories ?? []).filter(
    ({ id, icon_url: iconUrl }) => !!iconUrl && existing.get(id) !== iconUrl
  );

  if (additions.length === 0) return existing;

  const merged = new Map(existing);
  additions.forEach(({ id, icon_url: iconUrl }) => merged.set(id, iconUrl));

  return merged;
}

function mergeMap<T extends { id: string }>(
  existingMap: Map<string, T>,
  newItems: T[]
): Map<string, T> {
  const merged = new Map(existingMap);
  newItems.forEach((item) => merged.set(item.id, item));
  return merged;
}

export const useEntitiesStore = createWithEqualityFn<EntitiesState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      mergeEntities: (events, teams, entities, subCategories) => {
        const state = get();

        const changedEventIds = new Set<string>();
        for (const event of events) {
          if (hasEventChanged(state.eventsById.get(event.id), event)) {
            changedEventIds.add(event.id);
          }
        }

        const changedTeamIds = new Set<string>();
        for (const team of teams) {
          if (hasTeamChanged(state.teamsById.get(team.id), team)) {
            changedTeamIds.add(team.id);
          }
        }

        if (changedTeamIds.size > 0) {
          for (const event of state.eventsById.values()) {
            if (
              isPublicGameWithTeams(event) &&
              (changedTeamIds.has(event.home.id) || changedTeamIds.has(event.away.id))
            ) {
              changedEventIds.add(event.id);
            }
          }
        }

        const newEventsById = mergeMap(state.eventsById, events);
        const newTeamsById = mergeMap(state.teamsById, teams);
        const newEntitiesById = mergeMap(state.entitiesById, entities);
        const subCategoryIconUrlsById = mergeSubCategoryIconUrls(
          state.subCategoryIconUrlsById,
          subCategories
        );

        const normalizedTeamsByEventAndType = computeNormalizedTeamsIncremental(
          newEventsById,
          newTeamsById,
          state.normalizedTeamsByEventAndType,
          changedEventIds
        );

        set(
          {
            eventsById: newEventsById,
            teamsById: newTeamsById,
            entitiesById: newEntitiesById,
            subCategoryIconUrlsById,
            normalizedTeamsByEventAndType,
          },
          false,
          'mergeEntities'
        );
      },

      updateEvent: (eventId, eventUpdate) => {
        const state = get();

        const existingEvent = state.eventsById.get(eventId);
        if (!existingEvent) {
          return;
        }

        if (!isPublicGameWithTeams(existingEvent)) {
          return;
        }

        const updatedEvent = {
          ...existingEvent,
          ...(eventUpdate.status !== undefined && { status: eventUpdate.status }),
          ...(eventUpdate.state !== undefined && {
            state: { ...existingEvent.state, ...eventUpdate.state },
          }),
          home: {
            ...existingEvent.home,
            ...(eventUpdate.home ?? {}),
          },
          away: {
            ...existingEvent.away,
            ...(eventUpdate.away ?? {}),
          },
        } as typeof existingEvent;

        const newEventsById = new Map(state.eventsById);
        newEventsById.set(eventId, updatedEvent);

        const newNormalizedTeams = new Map(state.normalizedTeamsByEventAndType);

        const { homeTeam, awayTeam } = computeNormalizedTeamsForEvent(
          updatedEvent,
          state.teamsById
        );

        newNormalizedTeams.set(`${eventId}:home`, homeTeam);
        newNormalizedTeams.set(`${eventId}:away`, awayTeam);

        set(
          {
            eventsById: newEventsById,
            normalizedTeamsByEventAndType: newNormalizedTeams,
          },
          false,
          'updateEvent'
        );
      },

      updateEventsBatch: (updates) => {
        if (updates.length === 0) return;

        const state = get();

        const newEventsById = new Map(state.eventsById);
        const newNormalizedTeams = new Map(state.normalizedTeamsByEventAndType);

        for (const { eventId, update: eventUpdate } of updates) {
          const existingEvent = newEventsById.get(eventId);
          if (existingEvent && isPublicGameWithTeams(existingEvent)) {
            const updatedEvent = {
              ...existingEvent,
              ...(eventUpdate.status !== undefined && { status: eventUpdate.status }),
              ...(eventUpdate.state !== undefined && {
                state: { ...existingEvent.state, ...eventUpdate.state },
              }),
              home: {
                ...existingEvent.home,
                ...(eventUpdate.home ?? {}),
              },
              away: {
                ...existingEvent.away,
                ...(eventUpdate.away ?? {}),
              },
            } as typeof existingEvent;

            newEventsById.set(eventId, updatedEvent);

            const { homeTeam, awayTeam } = computeNormalizedTeamsForEvent(
              updatedEvent,
              state.teamsById
            );

            newNormalizedTeams.set(`${eventId}:home`, homeTeam);
            newNormalizedTeams.set(`${eventId}:away`, awayTeam);
          }
        }

        set(
          {
            eventsById: newEventsById,
            normalizedTeamsByEventAndType: newNormalizedTeams,
          },
          false,
          'updateEventsBatch'
        );
      },

      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'EntitiesStore' }
  ),
  shallow
);

export function useNormalizedTeam(
  eventId: string,
  teamType: 'home' | 'away'
): NormalizedTeam | undefined {
  return useEntitiesStore((state) =>
    state.normalizedTeamsByEventAndType.get(`${eventId}:${teamType}`)
  );
}

export function useEvent(eventId: string): PublicGame | undefined {
  return useEntitiesStore((state) => state.eventsById.get(eventId));
}

export function useTeam(teamId: string): PublicTeam | undefined {
  return useEntitiesStore((state) => state.teamsById.get(teamId));
}

export function useEntity(entityId: string): PublicEntity | undefined {
  return useEntitiesStore((state) => state.entitiesById.get(entityId));
}

export function useSubCategoryIconUrl(subCategory: string | undefined): string | undefined {
  return useEntitiesStore((state) =>
    subCategory ? state.subCategoryIconUrlsById.get(subCategory) : undefined
  );
}
